/**
 * RunBar: section 1 of the Screener page (Run, run time, run-saved line, data / quotes / mode chips, the run-used
 * line, Filters toggle, Download Excel, Clear). Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3,
 * 2026-10-28). The request, the run and the export stay in ScreenView; this component only calls them.
 */
import React from "react";
import { badgeStyle, cardStyle, mutedText, secondaryText, toneStyles, ui } from "@components/screener/screenerUi";
import { HelionButton } from "@components/ui/HelionButton";
import { runArchiveLine } from "@components/screener/runArchive";
import { summarizeDataWarnings, type ContextLoadStatus } from "@lib/screenerTruth";
import { Chip, type ChipTone } from "@components/cockpit";
import { toneFromDeploymentMode } from "@lib/screenerViewHelpers";
import { type DataConfidence, type ScreenerRequest, type ScreenerResponse } from "@lib/screenerTypes";

export type RunBarProps = {
  canSubmit: boolean;
  data: ScreenerResponse | null;
  dataChip: { text: string; tone: ChipTone; };
  dataConfidence: DataConfidence | null;
  error: string | null;
  exportToExcel: () => void;
  filtersPanel: React.JSX.Element;
  loading: boolean;
  mlWeight: number | null;
  quotesChip: { text: string; tone: ChipTone; };
  requester: string | null;
  riskContextStatus: ContextLoadStatus;
  runAsOfLine: string;
  runScreener: (e?: React.FormEvent, overrides?: Partial<ScreenerRequest>) => Promise<void>;
  runUsed: { mode: string | null; line: string | null; };
  setData: React.Dispatch<React.SetStateAction<ScreenerResponse | null>>;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  showFilters: boolean;
};

export default function RunBar({
  canSubmit,
  data,
  dataChip,
  dataConfidence,
  error,
  exportToExcel,
  filtersPanel,
  loading,
  mlWeight,
  quotesChip,
  requester,
  riskContextStatus,
  runAsOfLine,
  runScreener,
  runUsed,
  setData,
  setShowFilters,
  showFilters,
}: RunBarProps) {
  return (
    <section data-screener-section="run-bar" data-testid="screener-run-bar" className="mb-4 rounded-xl p-3.5" style={cardStyle}>
      <form onSubmit={runScreener}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <HelionButton onClick={runScreener as any} disabled={!canSubmit}>
            {loading ? "Running…" : riskContextStatus === "loading" ? "Run Screener (loading context…)" : "Run Screener"}
          </HelionButton>
          <span className="text-sm" style={secondaryText} data-testid="screener-run-time">
            {loading ? "Running the screen…" : data ? `Run as of ${runAsOfLine}` : "No run yet this session"}
          </span>
          {(() => {
            const line = data ? runArchiveLine(data.runArchive) : null;
            return line ? (
              <span
                data-testid="run-archive-line"
                data-status={data?.runArchive?.status}
                title={line.text}
                className="max-w-[420px] truncate"
                style={{ ...badgeStyle(line.tone), display: "inline-block" }}
              >
                {line.text}
              </span>
            ) : null;
          })()}
          {data ? (
            <span data-testid="screener-data-status" title={dataConfidence?.warnings?.length ? summarizeDataWarnings(dataConfidence.warnings, { mlWeight }) : undefined}>
              <Chip tone={dataChip.tone}>{dataChip.text}</Chip>
            </span>
          ) : null}
          {data ? (
            <span data-testid="screener-quotes-chip" title="Contracts with a usable mid AND vega after quote hydration, of those resolved. Detail in Diagnostics.">
              <Chip tone={quotesChip.tone}>{quotesChip.text}</Chip>
            </span>
          ) : null}
          {/* Phase 1 (CIO decision 2): the mode the RUN used (meta.inputs.risk_context, the backend's resolved value). */}
          {data ? (
            <span style={badgeStyle(toneFromDeploymentMode(runUsed.mode))} data-testid="screener-mode-badge">
              Mode: {runUsed.mode || "not reported"}
            </span>
          ) : null}
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <span className="text-xs" style={mutedText}>
              {requester ? <>Signed in: </> : null}
              <span data-testid="screener-requester" style={requester ? { color: ui.textPrimary } : { color: "rgb(var(--c-warn))", fontWeight: 600 }}>
                {requester || "Not signed in — queueing blocked"}
              </span>
            </span>
            <HelionButton
              type="button"
              variant="secondary"
              onClick={() => setShowFilters((prev) => !prev)}
              aria-expanded={showFilters}
              data-testid="screener-filters-toggle"
            >
              {showFilters ? "Filters ▾" : "Filters ▸"}
            </HelionButton>
            {data && (
              <HelionButton onClick={exportToExcel as any} disabled={loading} variant="secondary" type="button">
                Download Excel
              </HelionButton>
            )}
            <HelionButton onClick={() => setData(null)} disabled={loading} variant="secondary" type="button">
              Clear
            </HelionButton>
          </span>
        </div>

        {data ? (
          <div className="mt-2 text-xs" style={secondaryText} data-testid="screener-run-used">
            {runUsed.line || "This run did not report the risk context it used."}
          </div>
        ) : null}
        {riskContextStatus === "failed" ? (
          <div className="mt-2 text-xs font-semibold" style={{ color: "rgb(var(--c-warn))" }} data-testid="screener-context-default-mode">
            Context unavailable — ranked in default mode. The regime / defense fields could not be read, so the
            engine&rsquo;s resolver will rank without them.
          </div>
        ) : null}

        {showFilters && filtersPanel}

        {error && (
          <div className="mt-3 text-sm" style={{ ...toneStyles.danger, borderRadius: 12, padding: "10px 12px" }}>
            {error}
          </div>
        )}
      </form>
    </section>
  );
}
